import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useGetAuditTrailById } from '@common/slices';
import Loader from '@components/loader/Loader';
import ActivityTemplate from '@components/widgets/activities/ActivityTemplate';
import { Stepper } from '@mui/material';
import { MAIN_THEME } from '@styles';
import { extractIconUrl } from '@utils/auditTrailConvert-utils';

import { AuditTrailType } from 'src/enums';
import { makeStyles } from 'tss-react/mui';

export interface IAuditTrailProps {
  title: string;
  onCloseAction?: () => void;
  opened: boolean;
  auditId?: string;
  types?: AuditTrailType[];
  excludeTypes?: AuditTrailType[];
  refreshKey?: number;
}

const useStyles = makeStyles()(() => ({
  container: {
    paddingTop: '15px',
    width: '100%'
  },
  sectionHeader: {
    width: '50%',
    margin: `0 auto`,
    borderBottom: `2px solid ${MAIN_THEME.color.black}`
  },
  sectionTitle: {
    textAlign: 'center',
    fontSize: `${MAIN_THEME.fontSize.sm}!important`,
    fontFamily: MAIN_THEME.font.regular,
    letterSpacing: 0,
    color: MAIN_THEME.color.black,
    opacity: 1,
    margin: '0 0 15px',
    display: 'block'
  },
  stepper: {
    minWidth: 'fit-content',
    overflowY: 'auto',
    textAlign: 'left',
    width: '85%',
    margin: '10px 0 10px 0',
    padding: '24px'
  },
  empty: {
    position: 'absolute',
    top: '50%',
    left: '60%',
    margin: '-15% 0 0 -20%',
    color: MAIN_THEME.color.white86
  }
}));

const AuditTrailComponent: React.FC<IAuditTrailProps> = ({
  title,
  opened,
  auditId,
  types,
  excludeTypes,
  refreshKey = 0
}) => {
  const { classes } = useStyles();
  const { id: auditTrailId } = useParams<{ id: string }>();
  const entityId = auditId ?? auditTrailId;
  const [getAuditTrail, { data, loading: auditTrailsLoading }] =
    useGetAuditTrailById();

  // Primitive deps only — array identity / unstable fetch fn must not re-trigger forever.
  const typesKey = types?.join(',') ?? '';
  const excludeTypesKey = excludeTypes?.join(',') ?? '';

  const activities = useMemo(() => {
    const trails = data?.core?.getEntityAuditTrails || [];

    return trails.map((auditTrail) => {
      const createdOn = new Date(auditTrail.createdOn);
      const localDate = createdOn.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      const localTime = createdOn.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const htmlBody = auditTrail.message.replace(
        /\d{2} \w+ \d{4} at \d{2}:\d{2}:\d{2}/,
        `${localDate} at ${localTime}`
      );

      return {
        id: auditTrail.id,
        createdAt: createdOn,
        htmlBody,
        iconUrl: extractIconUrl(htmlBody)
      };
    });
  }, [data]);

  useEffect(() => {
    if (!opened || !entityId) return;

    void getAuditTrail({
      variables: {
        id: entityId,
        types: types ?? null,
        excludeTypes: excludeTypes ?? null
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAuditTrail is unstable; types/excludeTypes via keys
  }, [opened, entityId, refreshKey, typesKey, excludeTypesKey]);

  const renderContent = () => {
    if (auditTrailsLoading) {
      return (
        <span className={classes.empty}>
          <Loader />
        </span>
      );
    }

    if (activities.length > 0) {
      return (
        <Stepper orientation="vertical" className={classes.stepper}>
          {activities.map((activity) => (
            <ActivityTemplate key={activity.id} activity={activity} />
          ))}
        </Stepper>
      );
    }

    return <span className={classes.empty}>{'No Data'}</span>;
  };

  return (
    <section className={classes.container}>
      <div className={classes.sectionHeader}>
        <h1 className={classes.sectionTitle}>{title}</h1>
      </div>
      {renderContent()}
    </section>
  );
};

export default AuditTrailComponent;
